/* ================================================================
   Keyboard Shortcuts - Global key bindings.
   Ctrl+S = Save, Ctrl+Z = Undo, Ctrl+Y = Redo, Delete = Delete
   ================================================================ */

import { useEffect } from 'react';
import { useEditorStore } from '../state/editorStore';

export default function KeyboardShortcuts() {
  const saveToLocal = useEditorStore(s => s.saveToLocal);
  const undo = useEditorStore(s => s.undo);
  const redo = useEditorStore(s => s.redo);
  const deleteSelected = useEditorStore(s => s.deleteSelected);
  const deselectAll = useEditorStore(s => s.deselectAll);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const isCtrl = e.ctrlKey || e.metaKey;
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';

      // Ctrl+S: Save
      if (isCtrl && e.key === 's') {
        e.preventDefault();
        saveToLocal();
        return;
      }

      // Ctrl+Z: Undo
      if (isCtrl && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }

      // Ctrl+Y or Ctrl+Shift+Z: Redo
      if ((isCtrl && e.key === 'y') || (isCtrl && e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        redo();
        return;
      }

      // Skip delete/escape if we're in an input field
      if (isInput) return;

      // Delete: Delete selected
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelected();
        return;
      }

      // Escape: Deselect all
      if (e.key === 'Escape') {
        e.preventDefault();
        deselectAll();
        return;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveToLocal, undo, redo, deleteSelected, deselectAll]);

  return null;
}
